import { useEffect } from "react";

/**
 * Watches the page for chapter section boundaries (`#chapter-i`, `#chapter-ii`,
 * `#chapter-iii`) and sets `<html data-chapter="i|ii|iii">` based on which
 * chapter occupies the viewport's reading line (~40% from the top).
 *
 * CSS variables defined per `[data-chapter]` then smoothly transition the
 * page palette as the user scrolls between chapters.
 */
export default function useChapterScroll() {
  useEffect(() => {
    const html = document.documentElement;
    const chapterIds = ["chapter-i", "chapter-ii", "chapter-iii"];

    const apply = () => {
      const probeY = window.innerHeight * 0.4;
      let current = "i"; // default = Navy palette (also applies on Hero)
      for (const id of chapterIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        // Once a chapter heading has scrolled above the probe line, it owns
        // the palette until the next chapter heading crosses it.
        if (rect.top <= probeY) {
          current = id.replace("chapter-", "");
        }
      }
      if (html.getAttribute("data-chapter") !== current) {
        html.setAttribute("data-chapter", current);
      }
    };

    apply();
    // rAF-throttled scroll listener — cheap and smooth.
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        apply();
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", apply);
    };
  }, []);
}
